import { useMemo, useState, type KeyboardEvent, type PointerEvent } from 'react';
import { Shape, Tooltip } from './ChartFrame';
import { fmtDateTime, fmtTime, linePath, linearScale, logScale, logTicks, nearestIndex, niceDomain, niceTicks, timeTicks, useWidth } from './scale';

export interface Point {
  t: number;
  v: number;
}

export interface LaneEvent {
  t: number;
  title: string;
  detail?: string;
}

export interface Lane {
  id: string;
  label: string;
  color: string;
  shape: 'circle' | 'square' | 'diamond' | 'triangle';
  events: LaneEvent[];
}

interface Props {
  data: Point[];
  label: string;
  color: string;
  format: (v: number) => string;
  height?: number;
  area?: boolean;
  log?: boolean;
  zero?: boolean;
  lanes?: Lane[];
  ariaLabel?: string;
  /** 'index' plots t as a sequence number (e.g. decision #) instead of a timestamp. */
  xMode?: 'time' | 'index';
  indexLabel?: string;
}

const M = { top: 12, right: 14, bottom: 26, left: 62 };
const LANE_H = 24;

/**
 * Single-series time chart (2px line, optional 10% wash) with a snapping
 * crosshair, plus optional event lanes underneath — one lane per agent, so
 * markers never share a plot with another agent's colour.
 */
export function TimeChart({ data, label, color, format, height = 220, area, log, zero, lanes = [], ariaLabel, xMode = 'time', indexLabel = 'decision' }: Props) {
  const [ref, width] = useWidth<HTMLDivElement>();
  const [hover, setHover] = useState<number | null>(null);
  const [laneHover, setLaneHover] = useState<{ lane: number; ev: LaneEvent; x: number; y: number } | null>(null);

  const plotH = height;
  const lanesH = lanes.length ? lanes.length * LANE_H + 8 : 0;
  const totalH = M.top + plotH + lanesH + M.bottom;
  const innerW = Math.max(40, width - M.left - M.right);

  const geom = useMemo(() => {
    if (data.length < 2) return null;
    const t0 = data[0].t;
    const t1 = data[data.length - 1].t;
    const x = linearScale(t0, t1, M.left, M.left + innerW);
    let lo = Math.min(...data.map((d) => d.v));
    let hi = Math.max(...data.map((d) => d.v));
    const useLog = log && lo > 0 && hi / lo > 6;
    let ticks: number[];
    let y;
    if (useLog) {
      y = logScale(lo / 1.08, hi * 1.08, M.top + plotH, M.top);
      ticks = logTicks(lo / 1.08, hi * 1.08, 4);
    } else {
      if (zero) lo = Math.min(0, lo);
      if (hi === lo) hi = lo + (Math.abs(lo) || 1);
      const pad = (hi - lo) * 0.06;
      const [d0, d1] = niceDomain(zero ? lo : lo - pad, hi + pad, 4);
      y = linearScale(d0, d1, M.top + plotH, M.top);
      ticks = niceTicks(d0, d1, 4);
    }
    const pts = data.map((d) => [x(d.t), y(d.v)] as [number, number]);
    const line = linePath(pts);
    const base = y.domain[0] > 0 && !useLog ? y(y.domain[0]) : M.top + plotH;
    const fill = area ? `${line}L${pts[pts.length - 1][0].toFixed(1)},${base}L${pts[0][0].toFixed(1)},${base}Z` : '';
    const tickCount = Math.max(2, Math.floor(innerW / 110));
    const tt =
      xMode === 'index'
        ? { ticks: niceTicks(t0, t1, tickCount).filter((v) => Number.isInteger(v)), step: 0 }
        : timeTicks(t0, t1, tickCount);
    return { x, y, ticks, line, fill, tt, pts };
  }, [data, innerW, plotH, log, zero, area, xMode]);

  if (!geom) {
    return (
      <div ref={ref} className="chart-empty" style={{ height: totalH }}>
        Collecting data…
      </div>
    );
  }

  const onMove = (e: PointerEvent<SVGSVGElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const px = e.clientX - rect.left;
    const py = e.clientY - rect.top;
    const laneTop = M.top + plotH + 8;
    if (lanes.length && py >= laneTop && py < laneTop + lanes.length * LANE_H) {
      const li = Math.floor((py - laneTop) / LANE_H);
      const lane = lanes[li];
      let best: LaneEvent | null = null;
      let bestD = 14;
      for (const ev of lane.events) {
        const d = Math.abs(geom.x(ev.t) - px);
        if (d < bestD) {
          bestD = d;
          best = ev;
        }
      }
      setLaneHover(best ? { lane: li, ev: best, x: geom.x(best.t), y: laneTop + li * LANE_H } : null);
      setHover(null);
      return;
    }
    setLaneHover(null);
    if (px < M.left || px > M.left + innerW) return setHover(null);
    setHover(nearestIndex(data, geom.x.invert(px)));
  };

  const onKey = (e: KeyboardEvent<SVGSVGElement>) => {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
    e.preventDefault();
    setHover((h) => {
      const cur = h ?? data.length - 1;
      return Math.max(0, Math.min(data.length - 1, cur + (e.key === 'ArrowLeft' ? -1 : 1)));
    });
  };

  const h = hover != null ? data[hover] : null;
  const last = geom.pts[geom.pts.length - 1];

  return (
    <div ref={ref} className="viz" style={{ height: totalH }}>
      <svg
        width={width}
        height={totalH}
        role="img"
        aria-label={ariaLabel ?? label}
        tabIndex={0}
        onPointerMove={onMove}
        onPointerLeave={() => {
          setHover(null);
          setLaneHover(null);
        }}
        onFocus={() => setHover(data.length - 1)}
        onBlur={() => setHover(null)}
        onKeyDown={onKey}
      >
        {geom.ticks.map((tv) => (
          <g key={tv}>
            <line className="grid" x1={M.left} x2={M.left + innerW} y1={geom.y(tv)} y2={geom.y(tv)} />
            <text className="tick" x={M.left - 8} y={geom.y(tv)} dy="0.32em" textAnchor="end">
              {format(tv)}
            </text>
          </g>
        ))}
        <line className="axis" x1={M.left} x2={M.left + innerW} y1={M.top + plotH} y2={M.top + plotH} />
        {area && <path d={geom.fill} fill={color} opacity={0.1} />}
        <path d={geom.line} fill="none" stroke={color} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
        <circle cx={last[0]} cy={last[1]} r={4} fill={color} stroke="var(--surface)" strokeWidth={2} />

        {lanes.map((lane, li) => {
          const y = M.top + plotH + 8 + li * LANE_H + LANE_H / 2;
          return (
            <g key={lane.id}>
              <line className="grid" x1={M.left} x2={M.left + innerW} y1={y} y2={y} />
              <text className="tick lane-label" x={M.left - 8} y={y} dy="0.32em" textAnchor="end">
                {lane.label}
              </text>
              {lane.events.map((ev, i) => {
                const ex = geom.x(ev.t);
                if (ex < M.left - 1 || ex > M.left + innerW + 1) return null;
                return <Shape key={i} shape={lane.shape} x={ex} y={y} r={4} color={lane.color} />;
              })}
            </g>
          );
        })}

        {geom.tt.ticks.map((t) => (
          <text key={t} className="tick" x={geom.x(t)} y={totalH - 8} textAnchor="middle">
            {xMode === 'index' ? `#${t}` : fmtTime(t, geom.tt.step)}
          </text>
        ))}

        {h && (
          <g pointerEvents="none">
            <line className="crosshair" x1={geom.x(h.t)} x2={geom.x(h.t)} y1={M.top} y2={M.top + plotH + lanesH} />
            <circle cx={geom.x(h.t)} cy={geom.y(h.v)} r={4.5} fill={color} stroke="var(--surface)" strokeWidth={2} />
          </g>
        )}
        {laneHover && (
          <circle
            pointerEvents="none"
            cx={laneHover.x}
            cy={laneHover.y + LANE_H / 2}
            r={8}
            fill="none"
            stroke="var(--ink-2)"
            strokeWidth={1}
          />
        )}
      </svg>
      {h && (
        <Tooltip x={geom.x(h.t)} y={geom.y(h.v)} width={width}>
          <strong>{format(h.v)}</strong>
          <span className="tt-row">
            <i className="tt-key" style={{ background: color }} />
            {label}
          </span>
          <span className="tt-time">{xMode === 'index' ? `${indexLabel} #${h.t}` : fmtDateTime(h.t)}</span>
        </Tooltip>
      )}
      {laneHover && (
        <Tooltip x={laneHover.x} y={laneHover.y - 40} width={width}>
          <strong>{laneHover.ev.title}</strong>
          {laneHover.ev.detail && <span className="tt-row">{laneHover.ev.detail}</span>}
          <span className="tt-time">{fmtDateTime(laneHover.ev.t)}</span>
        </Tooltip>
      )}
    </div>
  );
}
